import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { ResponsiveDeviceType } from "../../../../widget/layouts/ScreenMedia";
import UtilityHelper from "../../../../service/utilities/UtilityHelper";
import ForwardChatProvider from "./ForwardChatProvider";
import {
  ConversationSearchProfileList,
  ForwardMessageList,
} from "../../../../model/chats/ForwardProfileListModel";
import { sendForwardMessages } from "../../../../libraries/state_reducers/chats/ChatsReducer";

type ForwardChatContextType = {
  selectedProfiles: Array<string>;
  profileList: Array<ConversationSearchProfileList> | null;
  handleClickProfile: (detail: ConversationSearchProfileList) => void;

  showSearch: boolean;
  searchValue: string;
  handleOnChange: (name: string, value: string) => void;
  clearSearch: () => void;
  updateShowSearch: () => void;

  handleSend: (isMedia: boolean) => void;
};

const ForwardChatContext = createContext<ForwardChatContextType | undefined>(
  undefined
);

export const ForwardChatContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useDispatch();
  const type: number = ResponsiveDeviceType();

  const searchValueRef = useRef<string>("");
  const profileListRef = useRef<Array<ConversationSearchProfileList> | []>([]);

  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedProfiles, setSelectedProfiles] = useState<Array<string>>([]);

  const [profileList, setProfileList] =
    useState<Array<ConversationSearchProfileList> | null>(null);

  const reset = () => {
    setShowSearch(type === 3 ? true : false);

    setSearchValue("");
    searchValueRef.current = "";
    setSelectedProfiles([]);
  };

  const handleOnChange = (fieldName: string, fieldValue: string) => {
    fieldName = fieldName.toLowerCase();
    if (fieldName === `search-chat-forward`) {
      setSearchValue(fieldValue);
      searchValueRef.current = fieldValue;

      const filtered: Array<ConversationSearchProfileList> | undefined =
        profileListRef.current?.filter((profile) =>
          Object.values(profile)
            .join(" ")
            .toLowerCase()
            .includes(searchValueRef.current.toLowerCase())
        );

      setProfileList(filtered!);
    }
  };

  const clearSearch = () => {
    if (UtilityHelper.isEmpty(searchValueRef.current)) {
      updateShowSearch();
    }

    setProfileList(profileListRef.current);
    setSearchValue("");
    searchValueRef.current = "";
  };

  const updateShowSearch = () => {
    setShowSearch(!showSearch);
  };

  const getForwardProfiles = async () => {
    const profileListResult: Array<ConversationSearchProfileList> | null =
      await ForwardChatProvider.getForwardProfilesAPI();

    if (profileListResult != null) {
      setProfileList(profileListResult);
      profileListRef.current = profileListResult;
    }
  };

  const handleClickProfile = (detail: ConversationSearchProfileList) => {
    const cId: string = detail.conv_id!;

    if (!selectedProfiles.includes(cId)) {
      setSelectedProfiles([...selectedProfiles, cId]);
    } else {
      setSelectedProfiles(selectedProfiles.filter((id) => id !== cId));
    }
  };

  const handleSend = (isMedia: boolean = false) => {
    if (selectedProfiles.length > 0) {
      const selectedList: Array<ForwardMessageList> = [];
      profileListRef.current.forEach((profile) => {
        if (selectedProfiles.includes(profile.conv_id!)) {
          selectedList.push({
            account_id: profile.account_id,
            conv_id: profile.conv_id,
            key: profile.key,
            flag: profile.flag,
            members_id: "",
          });
        }
      });

      console.log(selectedList);
      dispatch(sendForwardMessages(selectedList));

      //dispatch(removeModal(ModalKeys.chat_forward));
      window.history.replaceState(null, "", window.location.pathname);
    }
  };

  useEffect(() => {
    reset();
    getForwardProfiles();
    // eslint-disable-next-line
  }, []);

  return (
    <ForwardChatContext.Provider
      value={{
        selectedProfiles,
        profileList,
        handleClickProfile,

        showSearch,
        searchValue,
        handleOnChange,
        clearSearch,
        updateShowSearch,

        handleSend,
      }}
    >
      {children}
    </ForwardChatContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const ForwardChatContextImport = () => {
  const context = useContext(ForwardChatContext);
  if (!context) {
    throw new Error(
      "ForwardChatContextImport must be used within a ForwardChatContextProvider"
    );
  }
  return context;
};
